import { useGridApiRef, Table } from '@repo/go-table';
import type { GridColumnResizeParams, GridEventListener } from '@repo/go-table';
import { Loader } from '@repo/go-ui';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { debounce } from 'throttle-debounce';

import type { AlertsManagerWidgetState } from '../AlertsManagerWidgetState';
import tableStyles from '../AlertsManagerTable.module.css';
import type { ViewStore } from '../stores/ViewStore';
import { isColumnsOrderModelEqual } from '../utils';

import type { AutoAnomaliesService } from './AutoAnomaliesService';
import { AutoAnomaliesTableSettings } from './AutoAnomaliesTableSettings';
import { AutoAnomaliesTableStore } from './AutoAnomaliesTableStore';
import { AutoAnomaliesTableToolbar } from './AutoAnomaliesTableToolbar';
import { AutoAnomaliesViewActionsBar } from './AutoAnomaliesViewActionsBar';
import { AUTO_ANOMALIES_CONTEXT_COLUMN_FIELDS } from './FIELD_TO_AUTO_ANOMALIES_COLUMN_TITLE_KEY';
import type { TAutoAnomalyRowData } from './types';
import { useColumnGroups } from './useColumnGroups';
import { useAutoAnomaliesColumns } from './useTableColumns';

const contextVisibilityOverride = (hidden: boolean): Record<string, boolean> => {
  if (!hidden) return {};
  return Object.fromEntries(AUTO_ANOMALIES_CONTEXT_COLUMN_FIELDS.map((f) => [f, false]));
};

type Props = {
  viewStore: ViewStore;
  service: AutoAnomaliesService;
  widgetState: AlertsManagerWidgetState;
};

export const AutoAnomaliesTable = observer(function AutoAnomaliesTable({ viewStore, service, widgetState }: Props) {
  const tableApi = useGridApiRef();
  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(null);

  const store = useMemo(() => new AutoAnomaliesTableStore(tableApi, service), [service, tableApi]);

  const columns = useAutoAnomaliesColumns(
    widgetState.timeZonesStore,
    service,
    widgetState.appParams.isAltNames,
    widgetState.defaultDepthUnit,
  );
  const columnGroups = useColumnGroups();
  const { displayManager } = store;

  const columnFields = useMemo(() => columns.map((c) => c.field), [columns]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleWidthChange = useCallback(
    debounce(300, ({ colDef: { field }, width }: GridColumnResizeParams) => {
      if (!displayManager) return;
      const cols = tableApi.current.getAllColumns();
      const updated: Record<string, number> = {};
      const columnsWidth = displayManager.getColumnsWidth();
      cols.forEach((column) => {
        if (column.field === field) {
          updated[field] = width;
        } else if (columnsWidth && !columnsWidth[column.field]) {
          updated[column.field] = column.computedWidth;
        }
      });
      displayManager.onWidthChange(updated);
    }),
    [displayManager, tableApi],
  );

  useEffect(() => {
    return store.init();
  }, [store]);

  const columnsOrderModel = displayManager?.columnsOrderModel;

  const handleColumnsOrderModelChange = useCallback(
    (orderModel: string[]) => {
      const isOrderModelValid =
        orderModel.length === columnFields.length && orderModel.every((field) => columnFields.includes(field));

        if (!isOrderModelValid) {
          return;
        }
        
      const hasSavedOrderModel = Boolean(columnsOrderModel?.length);
      const hasCustomSavedOrderModel = hasSavedOrderModel && !isColumnsOrderModelEqual(columnsOrderModel, columnFields);
      const isIncomingDefaultOrderModel = isColumnsOrderModelEqual(orderModel, columnFields);



      if (hasCustomSavedOrderModel && isIncomingDefaultOrderModel) {
        return;
      }

      displayManager?.onColumnsOrderModelChange(orderModel);
    },
    [columnFields, columnsOrderModel, displayManager],
  );

  useEffect(() => {
    const columnsWidth = displayManager?.getColumnsWidth();
    if (!displayManager || !columnsWidth) return;
    for (const column of tableApi.current.getAllColumns()) {
      if (columnsWidth[column.field] === column.width) continue;
      tableApi.current.setColumnWidth(column.field, columnsWidth[column.field]);
    }
  }, [tableApi, displayManager]);

  const onClipboardCopy: GridEventListener<'clipboardCopy'> = useCallback((_, event) => {
    event.defaultMuiPrevented = true;
  }, []);

  const getSelectedEntityIds = useCallback(() => {
    const selected = tableApi.current.getSelectedRows();
    return Array.from(selected.keys()).map((k) => String(k));
  }, [tableApi]);

  const columnsVisibilityModel = displayManager?.columnsVisibilityModel;

  const mergedVisibility = useMemo(() => {
    return {
      ...(columnsVisibilityModel ?? {}),
      ...contextVisibilityOverride(store.contextColumnsHidden),
    };
  }, [columnsVisibilityModel, store.contextColumnsHidden]);

  if (!store.isInitialized || !displayManager) {
    return <Loader />;
  }

  const themeClassName = widgetState.appParams.theme === 'dark' ? 'go-widget-theme-dark' : 'go-widget-theme-light';

  return (
    <div className={clsx('go-widgets-widget-wrapper', themeClassName)} ref={(r) => setWrapperElement(r)}>
      <div className="go-widgets-content" style={{ height: '100%', position: 'relative' }}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '0 16px 16px' }}>
          <AutoAnomaliesTableToolbar
            viewStore={viewStore}
            contextHidden={store.contextColumnsHidden}
            onToggleContext={() => store.toggleContextColumnsHidden()}
            onDelete={() => service.deleteRows(getSelectedEntityIds())}
            deleteDisabled={service.loading || store.selectedRows.length === 0}
            editing={service.uiState.editing}
            onToggleSelectionMode={() => store.toggleSelectionMode()}
          />
          <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
            <Table<TAutoAnomalyRowData>
              initialState={{
                sorting: { sortModel: displayManager.sortingModel },
                filter: { filterModel: displayManager.filterModel },
                columns: { columnVisibilityModel: mergedVisibility },
                pinnedColumns: displayManager.pinnedColumns,
              }}
              sortModel={displayManager.sortingModel}
              filterModel={displayManager.filterModel}
              checkboxSelection={store.isSelectionModeActive || service.uiState.editing}
              columnVisibilityModel={mergedVisibility}
              columnsOrderModel={displayManager.columnsOrderModel}
              onColumnVisibilityModelChange={displayManager.onColumnVisibilityChange}
              onColumnsOrderModelChange={handleColumnsOrderModelChange}
              onPinnedColumnsChange={displayManager.onPinChange}
              onFilterModelChange={displayManager.onFilterChange}
              onSortModelChange={displayManager.onSortChange}
              onColumnResize={handleWidthChange}
              onRowSelectionModelChange={(model) => store.setSelectedRows(model)}
              onClipboardCopy={onClipboardCopy}
              getRowId={(row) => row.id}
              apiRef={tableApi}
              className={tableStyles.table}
              columns={columns}
              rows={service.rows}
              columnGroupingModel={columnGroups}
              loading={service.loading}
            />
          </div>
        </div>
      </div>
      <div className="go-widgets-actions-bar-wrapper">
        <AutoAnomaliesViewActionsBar widgetState={widgetState} store={store} service={service} />
      </div>
      <AutoAnomaliesTableSettings
        displayManager={displayManager}
        columns={columns}
        open={store.isTableSettingsOpen}
        onClose={() => store.setIsTableSettingsOpen(false)}
        container={wrapperElement ?? undefined}
      />
    </div>
  );
});
